<template>
  <div>
    <div v-if="showTitle" @click="downloadLink = !downloadLink">
      <div class="text-subtitle-1 font-weight-bold pa-1 bg-grey-lighten-3 border cursor-pointer">
        <v-icon icon="mdi-menu-right" v-if="!downloadLink"/>
        <v-icon size="small" icon="mdi-menu-down" v-show="downloadLink" />
        <v-icon size="small" icon="mdi-link-variant" /> Save CSV Download Link</div>
    </div>
  </div>

  <div v-if="downloadLink">
    <form>
      <v-radio-group v-model="linkName" class="mt-2" hide-details>
        <v-radio :label="'Choose a random name'" value="1" density="compact" class="my-1" />
        <v-radio :label="'Enter a name'" value="2" density="compact" class="my-1" />
        <v-radio :label="'Update existing URL'" value="3" v-if="showExistingUrl" density="compact" class="my-1" />
      </v-radio-group>

      <v-text-field v-if="linkName == '2'" placeholder="Enter a name" variant="outlined" v-model="linkNewName" density="compact" hide-details  class="mt-2" />

      <v-select v-if="linkName == '3'" v-model="selectedUrlName" :items="shortUrls" item-title="keyword" item-value="keyword" label="Select a URL" density="compact" hide-details class="mt-2" />

      <v-checkbox v-model="isRequireLoginCheckbox" label="Require user login" class="my-1" density="compact" hide-details />

      <div v-if="isRequireLoginCheckbox" class="mt-2 pl-6">
        <v-checkbox v-model="allowUsersToChange" label="Allow user to access scans made by other users" density="compact" hide-details/>
        <div class="mt-3 mb-1">Email when users can contact you.</div>
        <v-text-field variant="outlined" type="email" placeholder="Email" v-model="userEmail" density="compact"  />
      </div>

      <div class="mt-1" v-if="showSaveDownloadLink">
        <div v-show="currentPlanId == 1" class="text-center text-warning"><small>This is an option for paid accounts only!</small>
        </div>
        <v-btn @click="saveDownloadLink" color="black" :disabled="currentPlanId == 1" class="w-100">Save Download Link</v-btn>
      </div>

      <div class="mt-2">
        <router-link v-if="showViewDownloadLink" :to="{name: 'account.shorturl'}" target="_blank" class="text-black text-decoration-underline">View My Download Link</router-link>
      </div>
    </form>
  </div>

  <v-dialog v-model="modalVisible" width="800">
    <v-card title="Download CSV">
      <v-card-text>
        <div v-show="!creatingLink" class="text-center break-word">
          <h4>Click on the link</h4>
          <a :href="shortUrl" target="_blank" class="text-info" v-if="!creatingLink">{{ shortUrl }}</a>
        </div>

        <div class="text-center" v-if="creatingLink">
          <h3 v-if="linkName != '3'">Creating download link</h3>
          <h3 v-if="linkName == '3'">Updating download link</h3>
          <v-icon icon="mdi-progress-helper" v-if="creatingLink"></v-icon>
        </div>

        <div class="text-center mt-2">
          <v-btn @click="modalVisible = false" color="black">Close</v-btn>
        </div>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import { ref, watch, onMounted } from "vue";
import flashMessenger from '../service/FlashMessenger';
import crApi from "../service/CrApi";
import securityService from "../service/SecurityService";
import UtilService from "../service/UtilService";

const props = defineProps({
  name: {
    type:String,
    default: '1'
  },
  shareUrl: {
    type: Object,
    default: {}
  },
  newName: {
    type: String,
    default: ''
  },
  requireLoginCheckbox: {
    type: Boolean,
    default: false
  },
  allowUsers: {
    type: Boolean,
    default: false
  },
  email: {
    type: String,
    default: ''
  },
  showTitle: {
    type: Boolean,
    default: true
  },
  showSaveDownloadLink: {
    type: Boolean,
    default: true
  },
  showViewDownloadLink: {
    type: Boolean,
    default: true
  },
  showExistingUrl: {
    type: Boolean,
    default: true
  },
  hideDownloadBtn: {
    type: Boolean,
    default: false
  },
  scanFilterStateV2: {
    type: Object,
    default: () => ({})
  },
  prefix: {
    type: [String, Number],
    default: ''
  },
});
const emit = defineEmits(['update:name', 'update:shareUrl']);
const shortUrl = ref('');
const modalVisible = ref(false);
const creatingLink = ref(false);
const currentPlanId = ref(null);
const shortUrls = ref([]);
const loadingUrls = ref(true);
const selectedUrlName = ref('');
const downloadLink = ref(false);
const linkName = ref(props.name);
const linkNewName = ref(props.newName);
const isRequireLoginCheckbox = ref(props.requireLoginCheckbox);
const userEmail = ref(props.email);
const allowUsersToChange = ref(props.allowUsers);
const showTitle = ref(props.showTitle);
const accountId = ref(null);
const adminId = ref(null);
const idPrefix = ref();

watch(() => showTitle.value, (newVal) => {
  if (!newVal) {
    downloadLink.value = true
  }
})

watch(() => selectedUrlName.value, (newVal) => {
  const selectedUrl = shortUrls.value.find((url) => url.keyword === newVal)
  if (selectedUrl && selectedUrl['urlParams']) {
    userEmail.value = selectedUrl['urlParams']['contact_email'] || ''
    allowUsersToChange.value = selectedUrl['urlParams']['user_id_confirmed'] === '1'
    isRequireLoginCheckbox.value = userEmail.value !== '' || allowUsersToChange.value
  }
})

watch([() => linkName.value, () => linkNewName.value,() => isRequireLoginCheckbox.value, () => allowUsersToChange.value, () => userEmail.value], () => {
  if(props.shareUrl) {
    const shareObj = props.shareUrl;
    shareObj['shareUrlNameType'] = linkName.value;
    shareObj["urlName"] = linkNewName.value;
    shareObj["requireLoginCheckbox"] = isRequireLoginCheckbox.value;
    shareObj["allowUsers"] = allowUsersToChange.value;
    shareObj["contactEmail"] = userEmail.value

    emit('update:shareUrl', shareObj);
  }
})

onMounted(()=> {
  load();
})

async function load() {
  try {
    const adminResponse = await crApi.requestV2('admin/retrieve/', {website_token: securityService.getToken()});
    if (adminResponse.status === 1 && adminResponse['admin'].length > 0) {
      currentPlanId.value = adminResponse['admin'][0]['planId'];
      accountId.value = adminResponse.admin[0].accountId;
      adminId.value = adminResponse.admin[0].adminId;
    } else {
      await flashMessenger.toastById(adminResponse.error || 'Failed to load admin data', 'warning');
    }
  } catch (error) {
    await flashMessenger.toastById('Error fetching admin details.', 'error');
  }

  try {
    const shortUrlResponse = await crApi.requestV2('short_url/retrieve/', {
      website_token: securityService.getToken(),
      type: 'scans'
    });
    if (shortUrlResponse.status === 1) {
      shortUrls.value = shortUrlResponse['shortUrl'] || [];
      loadingUrls.value = false;
    } else {
      await flashMessenger.toastById(shortUrlResponse.error || 'Failed to load short URLs', 'warning');
    }

    if (!props.showTitle) {
      downloadLink.value = true;
    }

    idPrefix.value = props.prefix == undefined ? "" : props.prefix + "-";
  } catch (error) {
    await flashMessenger.toastById('Error fetching short URLs.', 'error');
  }
}

async function saveDownloadLink() {
  if (creatingLink.value) {
    return;
  }

  if (linkName.value == '3' && !selectedUrlName.value) {
    await flashMessenger.toastById('URL_NAME_IS_MISSING', 'error');
    return;
  }
  creatingLink.value = true;
  modalVisible.value = true;
  shortUrl.value = '';
  if (currentPlanId.value != 1 && isRequireLoginCheckbox.value && userEmail.value != '') {
    let re = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    if (!re.test(String(userEmail.value).toLowerCase())) {
      modalVisible.value = false;
      creatingLink.value = false;
      await flashMessenger.toastById('INVALID_EMAIL', 'error');
      return;
    }
  }

  let globalVars = await crApi.requestV2('globalvar/retrieve/',{variableName: 'apiURL', website_token: securityService.getToken()});

  let apiUrl = '';
  if (globalVars.status == 1) {
    let filterParams = {};
    filterParams['cr_au_id_internal'] = accountId.value;
    filterParams['returnType'] = 'csv';
    if (Object.keys(props.scanFilterStateV2).length) {
      filterParams = Object.assign(filterParams, props.scanFilterStateV2.getParameters());
    }
    if (isRequireLoginCheckbox.value) {
      filterParams['short_url_type'] = 'scans';
      if (userEmail.value != '') {
        filterParams['contact_email'] = userEmail.value;
      }
      if (allowUsersToChange.value) {
        filterParams['user_id_confirmed'] = '1';
      }
      if (props.hideDownloadBtn) {
        filterParams['hide_download_btn'] = 1;
      }
      // let curPort = location.port == '0' || location.port == '' ? '' : ':' + location.port;
      // apiUrl = location.protocol + '//' + location.hostname + curPort + '/share/preview/?' + 'yourls_encrypt_query=1&q=' + JSON.stringify(filterParams);
      apiUrl = '/share/preview/?' + 'yourls_encrypt_query=1&q=' + JSON.stringify(filterParams);
    } else {
      apiUrl = '/api-v2/scan/retrieve/?' + UtilService.serializeParams(filterParams);
    }
  }

  let params = {};
  let sUrlResponse = null;
  if (linkName.value == '3' && (selectedUrlName.value != '' || selectedUrlName.value != null)) {
    let updateParams = {
      website_token: securityService.getToken(),
      keyword: selectedUrlName.value,
      url: apiUrl
    };
    sUrlResponse = await crApi.requestV2('short_url/update/', updateParams);
  } else {
    if (linkName.value == '2' && linkNewName.value != '') {
      params['keyword'] = linkNewName.value;
    }
    params['type'] = 'scans';
    params['url'] = apiUrl;
    params['website_token'] = securityService.getToken();
    sUrlResponse =  await crApi.requestV2('short_url/create/',params);

  }

  if (sUrlResponse.status == 1) {
    shortUrl.value = sUrlResponse.shortUrl;
    creatingLink.value = false;
  } else {
    creatingLink.value = false;
    modalVisible.value = false;
    await flashMessenger.toastById(sUrlResponse.error, 'error');
  }
}
</script>

<style scoped lang="scss">

:deep(.v-checkbox .v-selection-control) {
  min-height: unset;
}

</style>